import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box, Typography } from "@mui/material";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { callFn } from "./callable";
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
  getDocFromServer,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getFirebaseClient } from "./firebase";
import { getAuthErrorMessage } from "./authErrors";
import type { AdminRole, Membership } from "./types";
import { panelForRole, panelIdForRole, type PanelId } from "./panels";
import * as Sentry from "@sentry/react";

interface AuthState {
  user: User | null;
  membership: Membership | null;
  allMemberships: Membership[];
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  switchMembership: (membershipId: string) => Promise<void>;
  /**
   * Switch the active membership AND land the user in the panel that owns the
   * target role. Reloads the current panel when the role belongs here,
   * otherwise navigates to the sibling panel's configured address.
   * Throws (rather than silently doing nothing) when the pointer write fails
   * or the target panel's address is not configured for this environment.
   */
  switchMembershipAndNavigate: (membership: Membership) => Promise<void>;
  /**
   * True from the moment a deliberate switch starts until the page reloads or
   * navigates away (or the switch fails). While this is true no automatic
   * role selection may run — otherwise it would fight the deliberate switch
   * and write the pointer back before we finish leaving the page.
   */
  switchInProgress: boolean;
  /** Which panel this provider is running inside. */
  panel: PanelId;
}

const Ctx = createContext<AuthState | null>(null);

// ── Device-local active membership storage (keyed by uid) ────────────────
// localStorage is a short-lived cache of the active membership pointer.
// Firestore (users/{uid}.activeMembershipId) is always written first by
// switchMembership — it is the authoritative source of truth for which
// membership is active. On every auth-state-change we reconcile localStorage
// toward Firestore: if Firestore holds a different value than the local cache,
// the local cache is updated to match. This prevents a stale local value from
// causing 403s on server-side callable guards (which always read Firestore)
// even when the client-side membership snapshot passes the Gate.

function localKey(uid: string) {
  return `activeMembershipId:${uid}`;
}

function getLocalMembershipId(uid: string): string | null {
  try {
    return localStorage.getItem(localKey(uid)) ?? null;
  } catch {
    return null;
  }
}

function setLocalMembershipId(uid: string, membershipId: string): void {
  try {
    localStorage.setItem(localKey(uid), membershipId);
  } catch {}
}

function clearLocalMembershipId(uid: string): void {
  try {
    localStorage.removeItem(localKey(uid));
  } catch {}
}

export function AuthProvider({
  children,
  panel,
}: {
  children: ReactNode;
  /** Panel this app is: "katana-admin" | "chain-admin" | "pharmacy-admin". */
  panel: PanelId;
}) {
  const { auth, db } = getFirebaseClient();
  const [user, setUser] = useState<User | null>(null);
  const [activeMembershipId, setActiveMembershipId] = useState<string | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [allMemberships, setAllMemberships] = useState<Membership[]>([]);
  const [authReady, setAuthReady] = useState(false);
  const [membershipReady, setMembershipReady] = useState(true);
  const [allReady, setAllReady] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether we've finished resolving the initial membership id for
  // the current user (the async localStorage + optional Firestore fallback).
  const [idResolved, setIdResolved] = useState(true);

  // Ref to cancel async work that races against sign-out.
  const currentUidRef = useRef<string | null>(null);

  // Set while a deliberate (user-initiated) switch is running, so the
  // automatic role auto-select never races against it.
  const [switchInProgress, setSwitchInProgress] = useState(false);

  // Ref that mirrors activeMembershipId state so the cross-tab snapshot
  // callback can compare without re-subscribing on every pointer change.
  const activeMembershipIdRef = useRef<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      currentUidRef.current = u?.uid ?? null;
      setUser(u);
      setAuthReady(true);
      if (!u) {
        Sentry.setUser(null);
        setActiveMembershipId(null);
        setMembership(null);
        setAllMemberships([]);
        setMembershipReady(true);
        setAllReady(true);
        setIdResolved(true);
      } else {
        setMembershipReady(false);
        setAllReady(false);
        setIdResolved(false);
        // Always reconcile the active membership id toward Firestore on
        // auth-state-change. localStorage is applied optimistically so the
        // UI doesn't wait on the network read, but if Firestore holds a
        // different value (e.g. another tab called switchMembership since
        // this tab last loaded), localStorage and state are updated to
        // match. Firestore is always the winner — it is written first by
        // switchMembership, so if the two disagree, Firestore is newer.
        const uid = u.uid;
        const local = getLocalMembershipId(uid);
        if (local) {
          // Apply the cached value immediately so the UI can render without
          // waiting, but still kick off the Firestore read to reconcile.
          setActiveMembershipId(local);
          setIdResolved(true);
        }
        // Retry the pointer read once: right after a cross-panel handoff this
        // origin has no local cache at all, so a single transient failure
        // here would leave the user with no active membership and a bogus
        // "Access denied" on a panel they do qualify for.
        const readPointer = () =>
          getDoc(doc(db, "users", uid)).catch(async (err) => {
            if (currentUidRef.current !== uid) throw err;
            await new Promise((r) => setTimeout(r, 400));
            if (currentUidRef.current !== uid) throw err;
            return getDoc(doc(db, "users", uid));
          });

        readPointer()
          .then((snap) => {
            if (currentUidRef.current !== uid) return;
            const firestoreId = snap.exists()
              ? (snap.data()?.activeMembershipId as string | undefined) ?? ""
              : "";
            if (!firestoreId) {
              // Firestore has no pointer yet — keep whatever we already applied.
              return;
            }
            if (firestoreId !== local) {
              // Firestore disagrees with local cache — reconcile toward Firestore.
              setLocalMembershipId(uid, firestoreId);
              setActiveMembershipId(firestoreId);
            }
          })
          .catch(() => {
            // Firestore read failed — keep the optimistic local value (or null
            // if there was none). Do not block the UI.
            if (currentUidRef.current !== uid) return;
            if (!local) setActiveMembershipId(null);
          })
          .finally(() => {
            if (currentUidRef.current === uid) setIdResolved(true);
          });
      }
    });
  }, [auth, db]);

  // Keep the ref in sync with state so the cross-tab snapshot callback
  // can compare incoming values without being listed as a dependency
  // (which would force the subscription to tear-down and re-create on
  // every pointer change).
  useEffect(() => {
    activeMembershipIdRef.current = activeMembershipId;
  }, [activeMembershipId]);

  // Cross-tab / cross-device sync: subscribe to users/{uid} so that when
  // another tab calls switchMembership (which writes activeMembershipId to
  // Firestore first), this tab's in-memory state is updated immediately
  // without a page reload. Without this, Tab 1 stays on the old membership
  // and the next callable it fires will 403 because the server reads the
  // Firestore pointer, which Tab 2 already updated.
  //
  // The ref comparison ensures we skip the setState call (and the cascade
  // of re-renders it would trigger) when the snapshot fires for a write
  // that originated in THIS tab — i.e. when the incoming value matches
  // what we already have in state.
  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    const unsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists()) return;
        const incoming =
          (snap.data()?.activeMembershipId as string | undefined) ?? null;
        if (!incoming) return;
        if (incoming === activeMembershipIdRef.current) return;
        // Another tab switched membership — reconcile toward Firestore.
        setLocalMembershipId(uid, incoming);
        setActiveMembershipId(incoming);
      },
      (err) => {
        console.error("[auth] users doc snapshot error", err);
      },
    );
    return unsub;
  }, [db, user]);

  // Subscribe to the active membership doc so revocation is detected in
  // real-time. The membership id comes from device-local state (resolved
  // from localStorage above) — not from a shared Firestore pointer
  // subscription, so another tab/device writing that pointer cannot change
  // which doc THIS tab is watching.
  useEffect(() => {
    if (!user || !idResolved) return;
    const uid = user.uid;

    if (!activeMembershipId) {
      setMembership(null);
      setMembershipReady(true);
      return;
    }

    setMembershipReady(false);
    const unsub = onSnapshot(
      doc(db, "memberships", activeMembershipId),
      (snap) => {
        const d = snap.exists()
          ? (snap.data() as Record<string, unknown>)
          : null;
        if (!d || d.uid !== uid) {
          setMembership(null);
        } else {
          Sentry.setUser({ id: uid, email: d.email as string | undefined });
          setMembership({
            uid,
            role: d.role as AdminRole,
            status: (d.status as "active" | "revoked") ?? "revoked",
            chainId: d.chainId as string | undefined,
            pharmacyId: d.pharmacyId as string | undefined,
            displayName: d.displayName as string | undefined,
            email: d.email as string | undefined,
            membershipId: snap.id,
          });
        }
        setMembershipReady(true);
      },
      (err) => {
        console.error("[auth] membership snapshot error", err);
        setMembership(null);
        setMembershipReady(true);
      },
    );
    return unsub;
  }, [db, user, activeMembershipId, idResolved]);

  // Subscribe to ALL memberships for this user so multi-role users can
  // see and switch between their roles without signing out.
  useEffect(() => {
    if (!user) {
      setAllMemberships([]);
      setAllReady(true);
      return;
    }
    const uid = user.uid;
    const q = query(
      collection(db, "memberships"),
      where("uid", "==", uid),
      where("status", "==", "active"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items: Membership[] = snap.docs.map((d) => {
          const r = d.data() as Record<string, unknown>;
          return {
            uid,
            role: r.role as AdminRole,
            status: (r.status as "active" | "revoked") ?? "revoked",
            chainId: r.chainId as string | undefined,
            pharmacyId: r.pharmacyId as string | undefined,
            displayName: r.displayName as string | undefined,
            email: r.email as string | undefined,
            membershipId: d.id,
          };
        });
        setAllMemberships(items);
        setAllReady(true);
      },
      (err) => {
        console.error("[auth] allMemberships snapshot error", err);
        setAllMemberships([]);
        setAllReady(true);
      },
    );
    return unsub;
  }, [db, user]);

  // Switch the active membership by writing the new pointer to Firestore
  // first, then mirroring it into localStorage and React state.
  // The Firestore write is the source of truth for server-side callable
  // guards (users/{uid}.activeMembershipId). If it fails, the switch is
  // aborted — localStorage and state are NOT updated, so the UI stays on
  // the pre-switch membership. Updating activeMembershipId state causes
  // the membership subscription effect above to re-run, automatically
  // tearing down the old snapshot and starting a new one.
  const switchMembership = useCallback(
    async (membershipId: string) => {
      if (!user) throw new Error("Not signed in.");
      await setDoc(
        doc(db, "users", user.uid),
        { uid: user.uid, activeMembershipId: membershipId, updatedAt: serverTimestamp() },
        { merge: true },
      );
      setLocalMembershipId(user.uid, membershipId);
      setActiveMembershipId(membershipId);
    },
    [user, db],
  );

  // Switch the active membership and then land the user in the panel that
  // actually serves the target role. Each panel is a separate hosting site,
  // so a plain reload after switching to a foreign role would drop the user
  // straight back onto the "Access denied" screen.
  const switchMembershipAndNavigate = useCallback(
    async (target: Membership) => {
      if (!target.membershipId) throw new Error("Membership is missing an id.");
      const targetPanel = panelIdForRole(target.role);
      if (targetPanel === "mobile") {
        throw new Error(
          "This role is used in the CCS Pharmacy mobile app — there is no web panel for it.",
        );
      }
      const info = panelForRole(target.role);
      if (targetPanel !== panel && !info.baseUrl) {
        throw new Error(
          `The address for the ${info.label} panel is not configured for this environment. Ask an administrator to set its panel URL.`,
        );
      }

      // Latch BEFORE the pointer write. Writing a pointer that this panel
      // cannot serve (a cross-panel switch) makes the Gate's automatic role
      // auto-select see an invalid active membership; without this latch it
      // would immediately write the pointer back to a local-panel role, and
      // the destination site would then read the reverted pointer and show
      // "Access denied" — the classic "works on the second click" failure.
      setSwitchInProgress(true);
      try {
        await switchMembership(target.membershipId);

        // Make sure the pointer write is durable server-side before we leave
        // this page — otherwise the sibling panel may read the old pointer.
        if (user) {
          const snap = await getDocFromServer(doc(db, "users", user.uid));
          const stored = snap.data()?.activeMembershipId as string | undefined;
          if (stored !== target.membershipId) {
            throw new Error("Role switch did not save. Please try again.");
          }
        }

        if (targetPanel === panel) {
          window.location.reload();
          return;
        }

        // The destination panel is a different Firebase Hosting origin, so
        // this browser's Firebase Auth session (origin-scoped) does not carry
        // over. Mint a one-shot custom token for our own uid and hand it to
        // the destination via the URL fragment (never sent to any server) so
        // it can sign itself in with signInWithCustomToken before rendering.
        let handoffToken: string;
        try {
          const res = await callFn<Record<string, never>, { token: string }>(
            "mintPanelHandoffToken",
            {},
          );
          handoffToken = res.token;
        } catch (err) {
          console.error("[auth] mintPanelHandoffToken failed", err);
          throw new Error(
            "Could not prepare sign-in for the other panel. Please try again.",
          );
        }
        const url = new URL(info.baseUrl!, window.location.href);
        url.hash = `ccsHandoff=${encodeURIComponent(handoffToken)}`;
        window.location.assign(url.toString());
      } catch (err) {
        // Release the latch only on failure — on success we are reloading or
        // navigating away and must stay latched until the page unloads.
        setSwitchInProgress(false);
        throw err;
      }
    },
    [panel, switchMembership, user, db],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      membership,
      allMemberships,
      loading: !authReady || !idResolved || !membershipReady || !allReady,
      error,
      async login(email, password) {
        setError(null);
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (e) {
          const msg = getAuthErrorMessage(e);
          setError(msg);
          throw new Error(msg);
        }
      },
      async logout() {
        if (user) clearLocalMembershipId(user.uid);
        await signOut(auth);
      },
      async resetPassword(email) {
        setError(null);
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (e) {
          const msg = getAuthErrorMessage(e);
          setError(msg);
          throw new Error(msg);
        }
      },
      switchMembership,
      switchMembershipAndNavigate,
      switchInProgress,
      panel,
    }),
    [user, membership, allMemberships, authReady, idResolved, membershipReady, allReady, error, auth, switchMembership, switchMembershipAndNavigate, switchInProgress, panel],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

/**
 * Result of the automatic role auto-select pre-check performed before a panel
 * shows its "Access denied" screen.
 */
export interface AutoSelectRoleState {
  /**
   * True while we are resolving/performing an automatic switch to a
   * qualifying membership. Panels should keep showing their loading state
   * (never the deny screen) while this is true.
   */
  switching: boolean;
  /**
   * True once we have concluded that no other active membership satisfies the
   * current panel — i.e. the deny screen is genuinely correct.
   */
  denied: boolean;
}

/**
 * Auto-select a valid role instead of blocking with "Access denied".
 *
 * When the user's ACTIVE membership doesn't satisfy the current panel but
 * another active membership does, switch the active pointer to that
 * membership and let the existing snapshot subscription re-render the panel.
 * Only the existing `switchMembership` is used — no new pointer logic, no
 * navigation (the qualifying membership belongs to this same panel by
 * definition of the predicate).
 *
 * The attempt is made at most once per mismatch so a failing or non-taking
 * switch can never loop.
 *
 * @param isValid Predicate describing "is this membership usable on the panel
 *   we're currently rendering". It MUST include the role check, so memberships
 *   belonging to other panels are never auto-selected.
 */
export function useAutoSelectValidRole(
  isValid: (m: Membership) => boolean,
): AutoSelectRoleState {
  const { user, membership, allMemberships, loading, switchMembership, switchInProgress } =
    useAuth();
  const [switching, setSwitching] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  // Membership id of the switch we just performed. Until the membership
  // subscription actually delivers that doc, `membership` still holds the
  // pre-switch value — concluding "denied" in that window produces a
  // transient Access denied flash for a user who does qualify.
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Membership ids we've already tried to switch to, so a switch that doesn't
  // resolve the mismatch never retries in a loop.
  const attemptedRef = useRef<Set<string>>(new Set());

  const currentOk = !!membership && isValid(membership);

  const candidate = useMemo(() => {
    if (currentOk) return null;
    return (
      allMemberships.find(
        (m) =>
          !!m.membershipId &&
          m.membershipId !== membership?.membershipId &&
          isValid(m) &&
          !attemptedRef.current.has(m.membershipId),
      ) ?? null
    );
    // `attemptedRef` is a ref: intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOk, allMemberships, membership, isValid]);

  // Reset the attempt bookkeeping when the signed-in user changes.
  useEffect(() => {
    attemptedRef.current = new Set();
    setExhausted(false);
    setSwitching(false);
    setPendingId(null);
  }, [user?.uid]);

  // Once the membership subscription catches up with the switch we made,
  // clear the pending marker so the normal denied/allowed logic resumes.
  useEffect(() => {
    if (!pendingId) return;
    // Settled either because the expected membership arrived, or because the
    // subscription finished for whatever the pointer now resolves to (the
    // doc may be missing/foreign). Clearing in both cases avoids an endless
    // spinner while still covering the in-between render.
    if (membership?.membershipId === pendingId) {
      setPendingId(null);
      return;
    }
    if (!switching && !loading) setPendingId(null);
  }, [pendingId, membership?.membershipId, switching, loading]);

  useEffect(() => {
    // A deliberate switch is in flight (user clicked "switch role"). Never
    // auto-select while it runs: doing so would write the pointer back
    // before the reload/handoff completes.
    if (switchInProgress) return;
    if (loading || !user || currentOk) return;
    // Still waiting for the membership doc from our own last switch.
    if (pendingId) return;
    if (!candidate?.membershipId) {
      setSwitching(false);
      setExhausted(true);
      return;
    }
    const id = candidate.membershipId;
    attemptedRef.current.add(id);
    setSwitching(true);
    setPendingId(id);
    let cancelled = false;
    switchMembership(id)
      .catch((err) => {
        console.error("[auth] auto role select failed", err);
        // The pointer never moved — drop the pending marker so the next
        // candidate (or the genuine deny) can be evaluated.
        if (!cancelled) setPendingId(null);
      })
      .finally(() => {
        if (!cancelled) setSwitching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user, currentOk, candidate, switchMembership, switchInProgress, pendingId]);

  if (currentOk) return { switching: false, denied: false };
  // While a deliberate switch is running, or while our own auto-switch is
  // still settling, keep the panel on its loading state rather than
  // flashing "Access denied".
  if (switchInProgress || pendingId) return { switching: true, denied: false };
  return { switching: switching || !exhausted, denied: exhausted && !switching };
}

export function RequireRole({
  role,
  children,
  fallback,
}: {
  role: AdminRole;
  children: ReactNode;
  fallback: ReactNode;
}) {
  const { user, membership, loading } = useAuth();
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }
  if (!user || !membership || membership.status !== "active" || membership.role !== role) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}
